import type { WidgetDef } from '../api';
import { linkProps } from '../router';
import { clubHref, dayRange, scApi, usePolling, useScSettings } from '../sportcenter';
import { ClubCard, FormChips, StandingsTable, Ticker } from './SportCenterBits';

type ScSource = Extract<WidgetDef['source'], { kind: 'sportcenter' }>;

/** Sportwidgets op het startscherm: ze verversen zelf (vaker tijdens live wedstrijden). */
export function SportWidget({ w, preview }: { w: WidgetDef; preview: boolean }) {
  const s = w.source as ScSource;
  if (s.list === 'ticker') return <TickerWidget title={w.title} preview={preview} />;
  return <ClubWidget s={s} title={w.title} preview={preview} />;
}

function TickerWidget({ title, preview }: { title: string; preview: boolean }) {
  const [from, to] = dayRange(0);
  const { data } = usePolling(`ticker:${from}`, () => scApi.scores(from, to));
  if (!data) return preview ? <p className="subtle">Laden…</p> : null;
  // Live eerst, dan je clubs, dan wat nog komt, dan de uitslagen van vandaag
  const rank = (m: (typeof data.matches)[number]) => (m.state === 'in' ? 0 : m.fav ? 1 : m.state === 'pre' ? 2 : 3);
  const list = [...data.matches].filter((m) => !m.cancelled).sort((a, b) => rank(a) - rank(b) || (a.state === 'post' ? b.start - a.start : a.start - b.start)).slice(0, 24);
  if (!list.length) return preview ? <p className="subtle">Vandaag geen wedstrijden in je competities.</p> : null;
  return <Ticker matches={list} title={title} />;
}

function ClubWidget({ s, title, preview }: { s: ScSource; title: string; preview: boolean }) {
  const settings = useScSettings();
  const first = settings?.teams[0];
  const [path, id] = s.team ? s.team.split('|') : first ? [first.path, first.id] : [null, null];
  const key = `scw:${path}:${id}`;
  const { data, error } = usePolling(key, async () => {
    if (!path || !id) throw new Error('none');
    const d = await scApi.team(path, id);
    return { ...d, poll: d.live ? 20 : 300 };
  });
  const tablePath = data?.team.path ?? '';
  const table = usePolling(`${key}:table:${s.league ?? ''}:${tablePath}`, async () => {
    let r: Awaited<ReturnType<typeof scApi.standings>> | null = null;
    if (s.list === 'table') {
      if (s.league) r = await scApi.standings({ league: s.league });
      else if (tablePath) r = await scApi.standings({ path: tablePath });
    }
    return { r, poll: 600 };
  });
  if (!settings) return null;
  if (!path) return preview ? <p className="subtle">Je volgt nog geen clubs. Kies ze bij <a {...linkProps('/sportcenter/kiezen')}>Sport → Clubs & competities</a>.</p> : null;
  if (error && !data) return preview ? <p className="error">{error}</p> : null;
  if (!data) return <section className="row widget-loading" aria-hidden="true"><h2>{title}</h2><div className="row-skeleton" /></section>;

  if (s.list === 'club') {
    return (
      <section className="row" aria-label={title}>
        <h2 className="row-title-link"><a {...linkProps(clubHref(data.team))}>{title} <span className="row-title-more" aria-hidden="true">›</span></a></h2>
        <div className="sc-widget-pad"><ClubCard s={data} /></div>
      </section>
    );
  }
  if (s.list === 'form') {
    return (
      <section className="row" aria-label={title}>
        <h2 className="row-title-link"><a {...linkProps(clubHref(data.team))}>{title} · {data.team.short} <span className="row-title-more" aria-hidden="true">›</span></a></h2>
        <div className="sc-widget-pad sc-widget-form"><FormChips form={data.form} big /></div>
      </section>
    );
  }
  const t = table.data?.r;
  if (!t) return preview ? <p className="subtle">Stand laden…</p> : null;
  if (!t.groups.length) return preview ? <p className="subtle">Voor deze competitie is geen stand.</p> : null;
  const around = t.groups.some((g) => g.rows.some((r) => r.team.id === data.team.id)) ? data.team.id : undefined;
  return (
    <section className="row" aria-label={title}>
      <h2>{title} · {t.league.name}</h2>
      <div className="sc-widget-pad sc-widget-table">
        <StandingsTable groups={t.groups} highlight={new Set(settings.teams.map((x) => x.id))} around={around} league={t.league.id} path={t.league.path} compact />
      </div>
    </section>
  );
}
